// ════════════════════════════════════════════════════════════════════
// TERROR — useTitleSync  (H9's one moving part)
// ────────────────────────────────────────────────────────────────────
// Mount this ONCE, at the top of App. It watches the player's
// achievement map and mints any title whose family just completed.
//
// ── WHY A TERROR-LOCAL HOOK AND NOT A LINE IN achievementWrites ──────
// achievementWrites.js, achievements.js, useAchievements.js,
// ieEconomy.js and markData.js are the VENDORED KIT — hand-copied
// byte-identical into every IE title, and audited by hash. A Terror-only
// import inside one of them registers as drift the next time that audit
// runs, and the audit is the only thing standing between five copies and
// five different files. So the kit stays untouched and the title layer
// hangs off it from outside. When titles go cross-game, THIS file is
// what gets promoted into the kit — one move, already isolated.
//
// ── WHY AN OVERSEER AND NOT A CALL AT EVERY GRANT SITE ───────────────
// There are twenty-odd grantAchievement() call sites. Nineteen of them
// would eventually be right. One listener over the map is right by
// construction, and it also catches achievements earned in OTHER IE
// titles — a cross-game family completes while you are in Terror and the
// title lands before you notice.
//
// ── COST ─────────────────────────────────────────────────────────────
// One listener that already exists in shape elsewhere, plus a Firebase
// read only when the completed-family SET actually grows. A player who
// has finished nothing new pays nothing.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { listenToAchievements } from "./achievementWrites";
import { earnedTitleIds, syncTitles, TITLE_BY_ID, textOf, tierOf } from "./titles";

// The signed-in uid, as state rather than a read. App does not carry one
// (it reaches for auth.currentUser at the moment it needs it), and a
// hook that mounts before sign-in has to LEARN the uid rather than miss
// it. Call it with an explicit uid to bypass this entirely — a surface
// that already has one should not open a second auth listener.
function useSelfUid(explicit) {
  const [uid, setUid] = useState(explicit || auth?.currentUser?.uid || null);
  useEffect(() => {
    if (explicit) { setUid(explicit); return; }
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
  }, [explicit]);
  return uid;
}

export function useTitleSync(explicitUid, { onMinted } = {}) {
  const uid = useSelfUid(explicitUid);
  // The set we have already reconciled. Seeded on the FIRST fire rather
  // than at mount, so a returning player does not get a toast storm for
  // titles they have worn for weeks. Same seeded-on-mount law the
  // mulligan announcement channel uses.
  const seen = useRef(null);
  const busy = useRef(false);
  const [minted, setMinted] = useState([]);

  // Keep the callback fresh without re-subscribing on every render.
  const cb = useRef(onMinted);
  cb.current = onMinted;

  useEffect(() => {
    if (!uid) { seen.current = null; return; }
    let dead = false;

    const unsub = listenToAchievements(uid, async (map) => {
      if (dead) return;
      const complete = earnedTitleIds(map || {});

      // FIRST FIRE — record, grant quietly, announce nothing.
      if (seen.current === null) {
        seen.current = new Set(complete);
        // Still sync: a family completed while the player was elsewhere
        // (or before this pipeline existed) must end up owned. It just
        // does not get a fanfare.
        if (complete.length && !busy.current) {
          busy.current = true;
          try { await syncTitles(uid, map || {}); }
          catch (e) { console.warn("[titles] backfill sync failed:", e?.message); }
          finally { busy.current = false; }
        }
        return;
      }

      const fresh = complete.filter((id) => !seen.current.has(id));
      if (!fresh.length) return;
      fresh.forEach((id) => seen.current.add(id));

      if (busy.current) return;      // a sync is already in flight; the
      busy.current = true;           // next map fire will catch the rest
      try {
        const got = await syncTitles(uid, map || {});
        if (dead || !got.length) return;
        const rows = got.map((id) => ({
          id,
          name: TITLE_BY_ID[id]?.name || id,
          text: textOf(id),
          tier: tierOf(id),
        }));
        setMinted(rows);
        if (typeof cb.current === "function") cb.current(rows);
      } catch (e) {
        // Non-fatal, always. A title that fails to mint is minted on the
        // next map fire; a title that throws takes the Plaza down with it.
        console.warn("[titles] sync failed:", e?.message);
      } finally {
        busy.current = false;
      }
    });

    return () => {
      dead = true;
      if (typeof unsub === "function") unsub();
    };
  }, [uid]);

  return minted;
}

export default useTitleSync;
